import { useEffect, useRef, useCallback, useState } from 'react'

const POLL_INTERVAL = 60_000 // 60 seconds
const RELOAD_GUARD_KEY = 'version-reload-guard'

export function useVersionCheck() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval>>()
  const notifiedRef = useRef(false)
  const currentHash = __APP_BUILD_HASH__

  const checkVersion = useCallback(async () => {
    if (notifiedRef.current) return
    try {
      const res = await fetch('/version.json', { cache: 'no-store' })
      if (!res.ok) return
      const data = await res.json()
      if (data.version && data.version !== currentHash) {
        notifiedRef.current = true
        setUpdateAvailable(true)
      }
    } catch {
      // Network error — silently ignore, will retry next interval
    }
  }, [currentHash])

  useEffect(() => {
    if (import.meta.env.DEV) return

    const initialTimer = setTimeout(checkVersion, 5_000)

    const startPolling = () => {
      stopPolling()
      timerRef.current = setInterval(checkVersion, POLL_INTERVAL)
    }

    const stopPolling = () => {
      if (timerRef.current) {
        clearInterval(timerRef.current)
        timerRef.current = undefined
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        checkVersion()
        startPolling()
      } else {
        stopPolling()
      }
    }

    if (document.visibilityState === 'visible') {
      startPolling()
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      clearTimeout(initialTimer)
      stopPolling()
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [checkVersion])

  const refresh = useCallback(() => {
    sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString())
    window.location.reload()
  }, [])

  return { updateAvailable, refresh }
}

/**
 * Check if an error is a chunk/dynamic-import load failure.
 */
export function isChunkLoadError(error: Error): boolean {
  const message = error.message || ''
  return (
    message.includes('Failed to fetch dynamically imported module') ||
    message.includes('Loading chunk') ||
    message.includes('Loading CSS chunk') ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * Returns true if we should auto-reload for a chunk error.
 * Returns false if we recently reloaded (prevents reload loops).
 */
export function shouldAutoReloadForChunkError(): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_GUARD_KEY)
  if (lastReload) {
    const elapsed = Date.now() - parseInt(lastReload, 10)
    if (elapsed < 30_000) return false
  }
  sessionStorage.setItem(RELOAD_GUARD_KEY, Date.now().toString())
  return true
}
